/* eslint-disable react-hooks/exhaustive-deps */
import {
  AnalyticMetricEnum,
  AnalyticMetricRecord,
  Company,
  getMetricCallback,
  Metric,
  MetricClass,
  MetricDimension,
} from '@laborability/commons';
import { ANALYTIC_METRIC_LABEL } from './types';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useRecoilCallback } from 'recoil';
import { LBTProgressSpinner } from '@laborability/components';

interface Props {
  currentMetric: AnalyticMetricEnum;
  borderColor: string;
  companyId: number | null;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  comparisonStartDate: dayjs.Dayjs | null;
  comparisonEndDate: dayjs.Dayjs | null;
  companies?: Company[];
}

export default function Table({
  borderColor,
  currentMetric,
  companyId,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  companies,
}: Props) {
  const getMetric = useRecoilCallback(getMetricCallback, []);
  const [sortedComparisionDimensions, setSortedComparisionDimensions] =
    useState<MetricDimension[]>([]);
  const [sortedDimensions, setSortedDimensions] = useState<MetricDimension[]>(
    [],
  );
  const [metricData, setMetricData] = useState<Metric>(new MetricClass());
  const [metricLoader, setMetricLoader] = useState<number>(0);
  const [comparisonMetricData, setComparisonMetricData] =
    useState<Metric | null>(null);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}'${minutes}''`;
  };

  const getPositionChange = (dimensionName: string, index: number) => {
    if (!comparisonMetricData) return null;
    const comparisonIndex = sortedComparisionDimensions.findIndex(
      dim => dim.name === dimensionName,
    );
    if (comparisonIndex === -1) return '-';
    const difference = comparisonIndex - index;
    if (difference === 0) return '-';
    return difference > 0 ? `↑${difference}` : `↓${Math.abs(difference)}`;
  };

  const getMetricData = async () => {
    if (!companyId && !companies?.length) return;
    setMetricLoader(val => val + 1);
    const res = await getMetric({
      metric: currentMetric,
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
      company_id: companyId,
    });
    setMetricLoader(val => val - 1);
    if (!res || !res?.data) return;
    setMetricData(res.data);
  };

  const getComparisonMetricData = async () => {
    if (!companyId && !companies?.length) return;
    if (!comparisonStartDate || !comparisonEndDate)
      return setComparisonMetricData(null);
    setMetricLoader(val => val + 1);
    const res = await getMetric({
      metric: currentMetric,
      start_date: comparisonStartDate.format('YYYY-MM-DD'),
      end_date: comparisonEndDate.format('YYYY-MM-DD'),
      company_id: companyId,
    });
    setMetricLoader(val => val - 1);
    if (!res || !res?.data) return;
    setComparisonMetricData(res.data);
  };

  useEffect(() => {
    const sorted = metricData.dimensions.slice().sort((a, b) => {
      const totalA = a.data.reduce((sum, item) => sum + item.value, 0);
      const totalB = b.data.reduce((sum, item) => sum + item.value, 0);
      return totalB - totalA;
    });
    setSortedDimensions(sorted);

    if (comparisonMetricData) {
      const sortedComparison = comparisonMetricData.dimensions
        .slice()
        .sort((a, b) => {
          const totalA = a.data.reduce((sum, item) => sum + item.value, 0);
          const totalB = b.data.reduce((sum, item) => sum + item.value, 0);
          return totalB - totalA;
        })
        .filter(dimension => sorted.find(dim => dim.name === dimension.name));
      setSortedComparisionDimensions(sortedComparison);
    }
  }, [metricData, comparisonMetricData]);

  useEffect(() => {
    getMetricData();
  }, [startDate, endDate, companyId, JSON.stringify(companies)]);

  useEffect(() => {
    getComparisonMetricData();
  }, [comparisonStartDate, comparisonEndDate, companyId]);

  if (!!metricLoader) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <LBTProgressSpinner />
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '32px',
        border: `2px solid ${borderColor}`,
        borderRadius: '5px',
        minWidth: '300px',
      }}
    >
      <h1
        style={{
          textAlign: 'center',
          margin: '10px',
          fontSize: '1rem',
          fontWeight: 'normal',
          padding: '5px 15px',
        }}
      >
        {ANALYTIC_METRIC_LABEL[metricData.metric]}
      </h1>
      <div
        style={{
          maxHeight: '175px',
          overflowY: 'auto',
          paddingBottom: '5px',
          paddingLeft: '15px',
          paddingRight: '15px',
          scrollbarWidth: 'thin',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '20px',
            border: '1px solid #ddd',
          }}
        >
          <tbody>
            {sortedDimensions.map((dimension, index) => {
              const total = dimension.data.reduce(
                (sum, item) => sum + item.value,
                0,
              );
              const positionChange = getPositionChange(dimension.name!, index);

              return (
                <tr key={`${dimension.name}-${index}`}>
                  <td
                    style={{
                      padding: '10px 30px',
                      display: 'flex',
                      justifyContent: 'center',
                      height: '100%',
                      borderTop: index !== 0 ? '1px solid #ddd' : undefined,
                    }}
                  >
                    {index + 1}
                  </td>
                  <td
                    style={{
                      padding: '10px 30px',
                      borderLeft: '1px solid #ddd',
                      borderRight: '1px solid #ddd',
                      borderTop: index !== 0 ? '1px solid #ddd' : undefined,
                    }}
                  >
                    {dimension.name}
                  </td>
                  {positionChange ? (
                    <td
                      style={{
                        padding: '10px 30px',
                        display: 'flex',
                        justifyContent: 'center',
                        borderTop: index !== 0 ? '1px solid #ddd' : undefined,
                      }}
                    >
                      {positionChange}
                    </td>
                  ) : (
                    <td
                      style={{
                        padding: '10px 30px',
                        display: 'flex',
                        justifyContent: 'center',
                        borderTop: index !== 0 ? '1px solid #ddd' : undefined,
                      }}
                    >
                      {isNaN(total)
                        ? 0
                        : AnalyticMetricRecord[currentMetric].time
                          ? formatTime(total)
                          : total}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
